/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Button } from '@/components/ui/button'
import {
  useCreateCustomerMutation,
  useCustomerQuery,
  useCustomersQuery,
  useWalkInCustomer,
  type Customer,
} from '@/hooks/useCustomers'
import { useDebounce } from '@/hooks/useDebounce'
import Loading from '@/components/Loading'

type Props = {
  value: string | null
  onChange: (id: string | null, display?: string) => void
  placeholder?: string
  allowCreate?: boolean
}

const PAGE_SIZE = 20

export default function CustomerSelect({
  value,
  onChange,
  placeholder = 'Sélectionner un client',
  allowCreate = true,
}: Props) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const debounced = useDebounce(search, 300)
  const [page, setPage] = React.useState(1)
  const [items, setItems] = React.useState<Customer[]>([])

  React.useEffect(() => {
    if (!open) {
      setSearch('')
      setPage(1)
      setItems([])
    }
  }, [open])

  React.useEffect(() => {
    if (!open) return
    setPage(1)
    setItems([])
  }, [debounced, open])

  const { data, isFetching, status } = useCustomersQuery(
    {
      q: debounced || undefined,
      page,
      pageSize: PAGE_SIZE,
      order: 'desc',
    },
    open
  )

  const total = data?.total ?? 0
  const currentPageItems = data?.data ?? []
  const hasNextPage = page * PAGE_SIZE < total

  React.useEffect(() => {
    if (!open) return
    if (currentPageItems?.length) {
      setItems((prev) => {
        const seen = new Set(prev.map((c) => c.id))
        const merged = [...prev]
        for (const c of currentPageItems) {
          if (!seen.has(c.id)) merged.push(c)
        }
        return merged
      })
    } else if (page === 1 && status === 'success') {
      setItems([])
    }
  }, [currentPageItems, page, status, open])

  const { data: walkIn } = useWalkInCustomer()
  const createCustomer = useCreateCustomerMutation()

  const selectedInList = items.find((i) => i.id === value)
  const { data: selectedFromQuery } = useCustomerQuery(
    value ?? undefined,
    !!value && !selectedInList
  )
  const selectedLabel = selectedInList?.name ?? selectedFromQuery?.name

  const handleCreate = async () => {
    if (!allowCreate || !search.trim()) return
    const c = await createCustomer.mutateAsync({ name: search.trim() })
    // on ajoute localement pour apparaitre dans la liste
    setItems((prev) => [c, ...prev])
    onChange(c.id, c.name)
    setOpen(false)
  }

  const onScroll = (e: React.UIEvent<HTMLElement>) => {
    const el = e.currentTarget
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10
    if (nearBottom && hasNextPage && !isFetching) {
      setPage((p) => p + 1)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant='outline' className='w-full justify-between'>
          {value ? selectedLabel ?? 'Client sélectionné' : placeholder}
          <span className='opacity-60'>▼</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className='p-0 w-[320px]'>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder='Rechercher un client...'
            value={search}
            onValueChange={setSearch}
            autoFocus
          />
          <CommandList onScroll={onScroll}>
            <CommandEmpty>
              {allowCreate ? (
                <div className='p-3 text-sm'>
                  Aucun résultat.{' '}
                  <button className='underline' onClick={handleCreate}>
                    Créer “{search.trim()}”
                  </button>
                </div>
              ) : (
                <div className='p-3 text-sm'>Aucun résultat.</div>
              )}
            </CommandEmpty>
            {walkIn && !debounced && (
              <CommandGroup heading='Vente rapide'>
                <CommandItem
                  key={walkIn.id}
                  value={walkIn.name}
                  onSelect={() => {
                    onChange(walkIn.id, walkIn.name)
                    setOpen(false)
                  }}
                >
                  <div className='flex flex-col'>
                    <span className='font-semibold'>{walkIn.name}</span>
                    <span className='text-xs text-muted-foreground'>
                      Pas de solde — monnaie toujours rendue
                    </span>
                  </div>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading='Clients'>
              {items
                .filter((c) => c.id !== walkIn?.id)
                .map((c) => (
                  <CommandItem
                    key={c.id}
                    value={c.name}
                    onSelect={() => {
                      onChange(c.id, c.name)
                      setOpen(false)
                    }}
                  >
                    <div className='flex flex-col'>
                      <span className='font-medium'>{c.name}</span>
                      <span className='text-xs text-muted-foreground'>
                        {c.phone ?? c.email ?? '—'}
                      </span>
                    </div>
                  </CommandItem>
                ))}
            </CommandGroup>
            {/* petit indicateur de chargement */}
            {isFetching ? <Loading /> : null}
          </CommandList>
          {allowCreate && search.trim() && (
            <div className='border-t p-2'>
              <Button className='w-full' size='sm' onClick={handleCreate}>
                Créer “{search.trim()}”
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}
